import {
  Component,
  ElementRef,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { TBulkEditCustomFields, TBulkEditProps } from 'react-lib/components/organisms/BulkEdit';
import { TLayoutConfig } from 'react-lib/components/organisms/CustomFieldGroupsSettings';
import { CustomFieldsLayoutApiProviderService } from 'src/app/project/data-providers/api-providers/custom-fields-provider/custom-fields-provider';
import { CustomFieldsService } from 'src/app/project/modules/custom-fields/custom-fields.service';
import { DEFAULT_DATE_FORMAT } from 'src/app/project/modules/preferences/default-date-format';
import { PreferencesService } from 'src/app/project/modules/preferences/preferences-service/preferences.service';
import { UsersService } from 'src/app/project/modules/users/users.service';
import { buildPointAssignees } from 'src/app/project/modules/points/point-card/point-card-assignees.utils';
import { logErrorInSentry } from 'src/app/project/modules/errors/response-error';
import BulkChangesBar, {
  TBulkChangesBarProps,
} from 'react-lib/components/organisms/BulkChangesBar';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { translate } from 'src/app/project/features/translate/translate';

import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { TUser } from 'src/app/project/modules/user/user.model';
import { TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { TWorkspace } from '../../../workspace/workspace.model';
import { SiteOptionsMoreFloatingActionsEditDropdownComponent } from './site-options-more-floating-actions-edit-dropdown/site-options-more-floating-actions-edit-dropdown.component';

import { ActiveService } from 'src/app/project/services/active/active.service';
import { ModalService } from '../../../../components/modal/modal.service';
import { SiteTableEventsService } from '../../site-table/site-table-events.service';

import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { BulkEditService } from 'src/app/project/modules/points/bulk-edit/bulk-edit.service';
import { findCommonTags } from 'src/app/project/modules/points/bulk-edit/find-common-tags';
import { PointsService } from 'src/app/project/modules/points/points.service';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { BulkCustomFieldsModalComponent } from '../../../custom-fields/bulk-custom-fields-modal/bulk-custom-fields-modal.component';
import { PointBulkAssigneesModalComponent } from '../../../points/point-modal/point-assignees/point-bulk-assignees-modal/point-bulk-assignees-modal.component';
import { PointBulkPriorityModalComponent } from '../../../points/point-priority/point-bulk-priority-modal/point-bulk-priority-modal.component';
import { PointBulkStatusModalComponent } from '../../../points/point-status/point-bulk-status-modal/point-bulk-status-modal.component';
import { BulkTagsModalComponent } from '../../../tags/bulk-tags-modal/bulk-tags-modal.component';
import { PointsCopyMoveModalComponent } from '../points-copy-move-modal/points-copy-move-modal.component';
import { PointsDeleteModalComponent } from '../points-delete-modal/points-delete-modal.component';

import { PointExportModalComponent } from 'src/app/project/modules/exports/point-export-modal/point-export-modal.component';
import { TExportModalData } from 'src/app/project/modules/exports/utils/export-modal-data';
import {
  canDeletePoint,
  canEditFlag,
  canEditPoint,
} from 'src/app/project/modules/share/share-utils/share-permissions';
import { EUserRole } from 'src/app/project/modules/share/share-utils/user-roles';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { checkCustomWorkspaceId } from '../../../workspace/workspace';
import { EWorkspaces } from '../../../workspace/workspaces.enum';
import { EModule } from 'src/app/project/shared/enums/module.enum';
import { EStore } from '../../../../shared/enums/store.enum';
import { PointBulkFlagModalComponent } from '../../../points/point-flag-modal/point-flag-modal.component';
import { isPointFlagged } from '../../../points/utils/is-flagged';
import { CustomTableService } from '../../site-table/custom-table/custom-table.service';
import { ECopyMoveModalType } from '../points-copy-move-modal/copy-move-modal-type..enum';
import {
  TPointsCopyModalData,
  TPointsMoveModalData,
} from '../points-copy-move-modal/points-copy-move-modal.model';
import { BULK_CHANGES_BAR_BODY_CLASS } from './bulk-changes-bar-body-class.const';
import { EFloatingActionsOptions } from './floating-actions-options.const';
import { runAfterNextPaint } from './run-after-next-paint';

@Component({
  selector: 'pp-floating-actions',
  templateUrl: './site-options-more-floating-actions.component.html',
  styleUrls: ['./site-options-more-floating-actions.component.scss'],
  standalone: false,
})
export class SiteOptionsMoreFloatingActionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() ppNumberOfSelectedPoints = 0;
  ppHasRelatedPoints = input<boolean>(false);
  @ViewChild('dropup', { static: true }) dropup: ElementRef;

  private dropdown: TDropdown = this.dropdownService.getDropdown();
  EUserRole = EUserRole;

  private readonly destroy$ = new Subject<void>();

  user$: Observable<TUser>;
  user: TUser;
  private workspaces$: Observable<TWorkspacesById>;
  workspace: TWorkspace;
  private canEditTags = false;
  canExport = true;
  private canEditCustomFields = false;
  normalWithPermissions = false;
  hovered: string = null;
  canDeletePoint = false;
  canEditPoint = false;
  private workspaceId = this.activeService.getActiveWorkspaceId();
  EIconPath = EIconPath;
  haveDifferentFlags = false;
  private isSelectionFlagged = false;
  private customFieldsLayout: TLayoutConfig[] | undefined = undefined;
  canEditFlag: boolean;
  isActionsFlagLoaded = false;
  isReactActionsEnabled = false;
  isBarClosing = false;
  private isDestroyed = false;
  BulkChangesBar = BulkChangesBar;
  barProps: TBulkChangesBarProps = this.buildBarProps();

  constructor(
    private store: Store<{
      user: TUser;
      workspaces: TWorkspacesById;
    }>,
    private modalService: ModalService,
    private activeService: ActiveService,
    private siteTableEventsService: SiteTableEventsService,
    private permissionsService: PermissionsService,
    private dropdownService: DropdownService,
    private pointsService: PointsService,
    private customTableService: CustomTableService,
    private featureFlagService: FeatureFlagService,
    private bulkEditService: BulkEditService,
    private customFieldsService: CustomFieldsService,
    private customFieldsLayoutApiProviderService: CustomFieldsLayoutApiProviderService,
    private usersService: UsersService,
    private preferencesService: PreferencesService,
  ) {
    this.user$ = this.store.pipe(select(EStore.USER));
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
  }

  ngOnInit() {
    this.loadActionsFlag();

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
      this.updateBarProps();
    });

    this.workspaces$.pipe(takeUntil(this.destroy$)).subscribe((workspaces) => {
      if (!workspaces || Object.keys(workspaces).length === 0) {
        return;
      }

      this.workspace = workspaces[this.workspaceId];

      // FIXME Can we just check `if (!this.workspace)` here? It would simplify the next code
      if (this.workspace === null) {
        this.canEditFlag = false;
        this.canEditTags = false;
        this.canEditCustomFields = false;
        this.canEditPoint = false;
        this.canDeletePoint = false;
        this.updateBarProps();

        return;
      }

      if (this.workspace) {
        this.canEditFlag = canEditFlag(this.workspace.share);
        this.canDeletePoint = canDeletePoint(this.workspace.share.shareOption);
        this.canEditPoint = canEditPoint(this.workspace.share.shareOption);

        const tagPermissions = this.permissionsService.getTagPermissions(
          this.workspace.workspaceId,
        );
        const customFieldPermissions = this.permissionsService.checkWorkspaceCustomFieldPermissions(
          this.workspace.workspaceId,
        );

        this.normalWithPermissions = true;
        this.canEditTags = tagPermissions.edit;
        this.canExport = this.permissionsService.areExportsEnabled(this.workspaceId);

        if (!this.canEditTags) {
          this.normalWithPermissions = false;
        }

        if (Object.keys(customFieldPermissions).length > 0) {
          Object.keys(customFieldPermissions).forEach((customField) => {
            if (customFieldPermissions[customField].edit) {
              this.canEditCustomFields = true;
            } else {
              this.normalWithPermissions = false;
            }
          });
        } else {
          this.canEditCustomFields = canEditPoint(this.workspace.share.shareOption);

          if (!this.canEditCustomFields) {
            this.normalWithPermissions = false;
          }
        }
      }

      this.updateBarProps();
    });
  }

  private async loadActionsFlag(): Promise<void> {
    await this.featureFlagService.whenReady();

    this.isReactActionsEnabled = this.featureFlagService.getFlag(EFeatureFlag.CONVERT_POINT_CARD);
    this.isActionsFlagLoaded = true;

    if (!this.isReactActionsEnabled || this.isDestroyed) {
      return;
    }

    document.body.classList.add(BULK_CHANGES_BAR_BODY_CLASS);
  }

  ngOnChanges() {
    if (!this.workspaceId) {
      const selectedWorkspaces = this.pointsService.getOverviewSelectedWorkspaces();
      let exportsEnabled = false;

      selectedWorkspaces.forEach((workspaceId) => {
        if (this.permissionsService.areExportsEnabled(workspaceId)) {
          exportsEnabled = true;
        }
      });

      this.canExport = exportsEnabled;
    }

    this.checkPointFlags();
    this.updateBarProps();
  }

  checkPointFlags(): void {
    const points = this.pointsService.getSelectedPoints();
    const initialPointFlag = points[0] ? isPointFlagged(points[0]) : false;
    this.isSelectionFlagged = initialPointFlag;

    for (const pointId of Object.keys(points)) {
      const pointFlag = isPointFlagged(points[pointId]);

      if (pointFlag !== initialPointFlag) {
        this.haveDifferentFlags = true;
        return;
      }
    }
    this.haveDifferentFlags = false;
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    document.body.classList.remove(BULK_CHANGES_BAR_BODY_CLASS);
    this.deselectPoints();
    this.destroy$.next();
  }

  bulkChangeFlag(): void {
    this.modalService.showModal(PointBulkFlagModalComponent);
  }

  bulkChangeStatus(): void {
    this.modalService.showModal(PointBulkStatusModalComponent);
  }

  bulkChangePriority(): void {
    this.modalService.showModal(PointBulkPriorityModalComponent);
  }

  bulkChangeAssignees(): void {
    this.modalService.showModal(PointBulkAssigneesModalComponent);
  }

  bulkChangeCustomFields(): void {
    this.modalService.showModal(BulkCustomFieldsModalComponent);
  }

  bulkAddTags(): void {
    this.modalService.showModal(BulkTagsModalComponent);
  }

  copyPoints(): void {
    this.modalService.setData<TPointsCopyModalData>({
      modalType: ECopyMoveModalType.COPY,
      workspaceId: this.workspaceId,
      numberOfPoints: this.ppNumberOfSelectedPoints,
      hasRelatedPoints: this.ppHasRelatedPoints(),
    });

    this.modalService.showModal(PointsCopyMoveModalComponent, {
      closeWarning: true,
    });
  }

  movePoints(): void {
    this.modalService.setData<TPointsMoveModalData>({
      modalType: ECopyMoveModalType.MOVE,
      workspaceId: this.workspaceId,
      hasRelatedPoints: this.ppHasRelatedPoints(),
    });

    this.modalService.showModal(PointsCopyMoveModalComponent, {
      closeWarning: true,
    });
  }

  deletePoints(): void {
    this.modalService.showModal(PointsDeleteModalComponent);
  }

  closeBulkChanges(): void {
    this.deselectPoints();
  }

  private hideBarAndDeselectPoints(): void {
    this.isBarClosing = true;
    runAfterNextPaint(() => this.deselectPoints());
  }

  deselectPoints(): void {
    if (this.workspaceId) {
      this.siteTableEventsService.deselectWorkspacePoints(this.workspaceId);
    } else {
      this.siteTableEventsService.deselectAllPoints();
    }

    this.customTableService.getTable()?.deselectPoints();
  }

  export(): void {
    if (this.canExport) {
      const workspaceId = this.activeService.getActiveWorkspaceId();

      const modalData: TExportModalData = {
        workspaceId,
        formats: {
          simpleList: true,
          detailedList: true,
          xlsxOutput: true,
          individualReports: true,
          wordPoints: true,
          sitePlan: true,
        },
      };

      this.modalService.setData(modalData);

      logEventInGTAG(EGoogleEventSite.SITE__BULK_CHANGES__EXPORT, {
        source: EGoogleEventCategory.SITE,
        count: this.ppNumberOfSelectedPoints,
      });

      this.modalService.showModal(PointExportModalComponent, {
        blur: true,
        props: {
          module:
            checkCustomWorkspaceId() === EWorkspaces.OVERVIEW ? EModule.overview : EModule.site,
        },
      });
    }
  }

  private updateBarProps(): void {
    this.barProps = this.buildBarProps();
  }

  private buildBarProps(): TBulkChangesBarProps {
    return {
      selectedCount: this.ppNumberOfSelectedPoints,
      onClose: (): void => this.hideBarAndDeselectPoints(),
      edit: this.canBulkEdit() ? this.buildEditProps() : undefined,
      onCopy: this.canCopyPoints() ? (): void => this.copyPoints() : undefined,
      onMove: this.canMovePoints() ? (): void => this.movePoints() : undefined,
      onExport: (): void => this.export(),
      exportDisabledReason: this.canExport ? undefined : translate('export_permission_denied'),
      onDelete: this.canDeletePoint ? (): void => this.deletePoints() : undefined,
    };
  }

  private buildEditProps(): TBulkEditProps {
    return {
      options: this.getEditOptions(),
      disabledReasons: { [EFloatingActionsOptions.FLAG]: this.getFlagDisabledReason() },
      isFlagged: this.isSelectionFlagged,
      customFields: this.buildCustomFieldsProps(),
      assigneeOptions: buildPointAssignees({
        assigneeIds: [],
        workspaceUserIds: this.workspace?.users ?? [],
        users: this.usersService.getUsers() ?? {},
      }).options,
      commonTags: findCommonTags(this.pointsService.getSelectedPoints()),
      tagOptions: this.workspace?.tags ?? [],
      onSave: (changes, eventType, mentions): Promise<void> =>
        this.bulkEditService.save(changes, eventType, mentions),
    };
  }

  private buildCustomFieldsProps(): TBulkEditCustomFields {
    return {
      layout: this.customFieldsLayout,
      workspaceCustomFields: this.customFieldsService.getCustomFields()?.[this.workspaceId] ?? {},
      customFieldPermissions: this.permissionsService.checkWorkspaceCustomFieldPermissions(
        this.workspaceId,
      ),
      canEditPoint: this.canEditPoint,
      users: this.usersService.getUsers() ?? {},
      dateFormat: this.preferencesService.getPreferences()?.dateFormat ?? DEFAULT_DATE_FORMAT,
      onOpen: (): void => this.loadCustomFieldsLayout(),
    };
  }

  private loadCustomFieldsLayout(): void {
    if (this.customFieldsLayout) {
      return;
    }

    this.customFieldsLayoutApiProviderService
      .getAllCustomFieldsLayout(this.workspaceId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          logErrorInSentry({
            error,
            message: 'Error fetching custom field layout on bulk changes',
          });
          this.setCustomFieldsLayout([]);

          return EMPTY;
        }),
      )
      .subscribe((response) => this.setCustomFieldsLayout(response.fieldsLayout));
  }

  private setCustomFieldsLayout(layout: TLayoutConfig[]): void {
    this.customFieldsLayout = layout;
    this.updateBarProps();
  }

  private getEditOptions(): EFloatingActionsOptions[] {
    const canEditWorkspaceCustomFields =
      this.workspace.customFields?.length > 0 && this.canEditCustomFields;
    const customFieldsOptions: EFloatingActionsOptions[] = canEditWorkspaceCustomFields
      ? [EFloatingActionsOptions.CUSTOM_FIELDS]
      : [];
    const tagsOptions: EFloatingActionsOptions[] = this.canEditTags
      ? [EFloatingActionsOptions.TAGS]
      : [];

    return [
      EFloatingActionsOptions.FLAG,
      EFloatingActionsOptions.PRIORITY,
      EFloatingActionsOptions.STATUS,
      ...customFieldsOptions,
      ...tagsOptions,
      EFloatingActionsOptions.ASSIGNEES,
    ];
  }

  private getFlagDisabledReason(): string | undefined {
    if (!this.canEditFlag) {
      return translate('flag_no_permission');
    }

    if (this.haveDifferentFlags) {
      return translate('flag_clash');
    }

    return undefined;
  }

  private canBulkEdit(): boolean {
    return !!this.workspace?.workspaceId && this.canEditPoint;
  }

  private canCopyPoints(): boolean {
    if (!this.workspace) {
      return false;
    }

    const isNormalWithPermissions =
      this.workspace.share.shareOption === EUserRole.NORMAL && this.normalWithPermissions;

    return this.isWorkspaceManager() || isNormalWithPermissions;
  }

  private canMovePoints(): boolean {
    return !!this.workspace?.workspaceId && this.isWorkspaceManager();
  }

  private isWorkspaceManager(): boolean {
    const shareOption = this.workspace?.share.shareOption;

    return (
      shareOption === EUserRole.OWNER ||
      shareOption === EUserRole.SITE_ADMIN ||
      shareOption === EUserRole.ACCOUNT_ADMIN
    );
  }

  buttonHover(id: string): void {
    if (id !== 'export__button' || this.canExport) {
      this.hovered = id;
    }
  }

  buttonHoverEnd(): void {
    this.hovered = null;
  }

  toggleOptionsDropdown(buttonId: string): void {
    if (this.dropdown.visible && this.dropdown.buttonId === buttonId) {
      this.dropdownService.hideDropdown();
    } else {
      const editingCustomFields =
        this.workspace &&
        this.workspace.customFields &&
        this.workspace.customFields.length &&
        this.canEditCustomFields;

      this.dropdownService.setData({
        canEditCustomFields: editingCustomFields,
        canEditTags: this.canEditTags,
        canEditFlag: this.canEditFlag,
        haveDifferentFlags: this.haveDifferentFlags,
      });

      this.dropdownService.showDropdown(
        buttonId,
        SiteOptionsMoreFloatingActionsEditDropdownComponent,
        {
          callback: (name) => this.openPopup(name),
          popper: {
            positionFixed: true,
            placement: 'top',
          },
          suppressScrollbar: true,
        },
      );
    }
  }

  openPopup(name: EFloatingActionsOptions): void {
    if (name === EFloatingActionsOptions.PRIORITY) {
      this.bulkChangePriority();
    } else if (name === EFloatingActionsOptions.STATUS) {
      this.bulkChangeStatus();
    } else if (name === EFloatingActionsOptions.CUSTOM_FIELDS) {
      this.bulkChangeCustomFields();
    } else if (name === EFloatingActionsOptions.TAGS) {
      this.bulkAddTags();
    } else if (name === EFloatingActionsOptions.ASSIGNEES) {
      this.bulkChangeAssignees();
    } else if (name === EFloatingActionsOptions.FLAG) {
      this.bulkChangeFlag();
    }
  }
}
